/**
 * Chunked file upload helper.
 *
 * Splits the file into fixed-size chunks and uploads them with bounded concurrency.
 * Reports cumulative byte progress.
 *
 *   uploadInChunks(file, { kind, onProgress, concurrency, chunkSize, signal })
 *     -> { id, url, original_filename, content_type, size }
 */
import api from "@/lib/api";

const DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024; // 8 MB
const DEFAULT_CONCURRENCY = 3;

export async function uploadInChunks(
  file,
  { kind = "video", onProgress, concurrency = DEFAULT_CONCURRENCY, chunkSize = DEFAULT_CHUNK_SIZE, signal } = {},
) {
  if (!file) throw new Error("No file provided");
  const totalSize = file.size;
  const totalChunks = Math.max(1, Math.ceil(totalSize / chunkSize));

  // 1) Init upload session
  const initResp = await api.post("/upload/init", {
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    total_size: totalSize,
    total_chunks: totalChunks,
    kind,
  });
  const { upload_id: uploadId, chunk_size: serverChunkSize } = initResp.data;
  // Honour server-suggested chunk size if it differs significantly
  const effectiveChunkSize = serverChunkSize || chunkSize;
  // Recompute totals if server enforces a different size
  const realTotalChunks = Math.max(1, Math.ceil(totalSize / effectiveChunkSize));

  // If server size differs and changes total chunks, re-init with correct count
  let activeUploadId = uploadId;
  if (realTotalChunks !== totalChunks) {
    // Best-effort: abort old session, re-init
    try { await api.delete(`/upload/abort/${uploadId}`); } catch { /* ignore */ }
    const re = await api.post("/upload/init", {
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      total_size: totalSize,
      total_chunks: realTotalChunks,
      kind,
    });
    activeUploadId = re.data.upload_id;
  }

  // 2) Track progress as bytes uploaded
  const sentBytes = new Array(realTotalChunks).fill(0);
  const reportProgress = () => {
    if (!onProgress) return;
    const sent = sentBytes.reduce((a, b) => a + b, 0);
    onProgress({
      loaded: sent,
      total: totalSize,
      percent: Math.min(100, Math.round((sent / totalSize) * 100)),
    });
  };

  // 3) Worker function: upload one chunk
  const uploadOneChunk = async (index) => {
    if (signal?.aborted) throw new Error("Aborted");
    const start = index * effectiveChunkSize;
    const end = Math.min(start + effectiveChunkSize, totalSize);
    const blob = file.slice(start, end);
    let attempt = 0;
    const maxAttempts = 4;
    // Retry with backoff on transient failures
    while (true) {
      try {
        await api.post(`/upload/chunk/${activeUploadId}/${index}`, blob, {
          headers: { "Content-Type": "application/octet-stream" },
          signal,
          onUploadProgress: (ev) => {
            if (typeof ev.loaded === "number") {
              sentBytes[index] = ev.loaded;
              reportProgress();
            }
          },
          // Allow large chunks
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        });
        sentBytes[index] = blob.size;
        reportProgress();
        return;
      } catch (err) {
        attempt += 1;
        if (signal?.aborted || attempt >= maxAttempts) throw err;
        const delay = Math.min(8000, 500 * Math.pow(2, attempt));
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  };

  // 4) Bounded-concurrency runner
  const indices = Array.from({ length: realTotalChunks }, (_, i) => i);
  const workers = Array.from({ length: Math.min(concurrency, realTotalChunks) }, async () => {
    while (indices.length > 0) {
      const idx = indices.shift();
      if (idx === undefined) return;
      await uploadOneChunk(idx);
    }
  });

  try {
    await Promise.all(workers);
  } catch (err) {
    try { await api.delete(`/upload/abort/${activeUploadId}`); } catch { /* ignore */ }
    throw err;
  }

  // 5) Complete
  const completeResp = await api.post(`/upload/complete/${activeUploadId}`);
  return completeResp.data;
}
